import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Alert
} from '@mui/material';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`webhook-test-tabpanel-${index}`}
      aria-labelledby={`webhook-test-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function WebhookTestModal({
  open,
  handleClose,
  webhookUrl,
  skuOptions = [], // Provide default to prevent map errors if undefined initially
  selectedSku,
  setSelectedSku,
  onTestWebhook, // Renamed to avoid confusion with internal handlers
  isLoading,
  testResponse,
  testSecret,
  setTestSecret,
  onVerifySignature,
  verificationResult,
  webhookType,
}) {
  const [activeTab, setActiveTab] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const hmacExampleCode = `import { HmacSHA256 } from 'crypto-js';

const payload = req.body;
const secret = 'YOUR_WEBHOOK_SECRET';
const { data, signature } = payload;

const calculatedSignature = HmacSHA256(data, secret).toString();

if (calculatedSignature === signature) {
  console.log('Signature is valid.');
} else {
  console.log('Invalid signature.');
}`;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        Test {webhookType === 'DEVICE_MEASUREMENT' ? 'Device Measurement' : 'Order Updates'} Webhook: 
        <Box 
          component="span" 
          sx={{
            backgroundColor: 'action.hover',
            padding: '2px 6px',
            borderRadius: '4px',
            fontFamily: 'monospace',
          }}
        >
          {webhookUrl}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleChangeTab} aria-label="webhook test tabs">
            <Tab label="Test Configuration & Response" id="webhook-test-tab-0" aria-controls="webhook-test-tabpanel-0" sx={{ textTransform: 'none' }} />
            <Tab label="HMAC Code Example" id="webhook-test-tab-1" aria-controls="webhook-test-tabpanel-1" sx={{ textTransform: 'none' }} />
          </Tabs>
        </Box>
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
            {webhookType === 'DEVICE_MEASUREMENT' && (
            <FormControl sx={{ minWidth: 220, mr: 2 }} size="small">
              <InputLabel id="sku-select-modal-label">Test Device SKU</InputLabel>
              <Select
                labelId="sku-select-modal-label"
                id="sku-select-modal"
                value={selectedSku}
                label="Test Device SKU"
                onChange={(e) => setSelectedSku(e.target.value)}
              >
                {skuOptions.map(({ sku, productName }) => (
                  <MenuItem value={sku} key={sku}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{sku}</Typography>
                      {productName && (
                        <Typography variant="caption" color="text.secondary">
                          {productName}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}
            <Button variant="contained" onClick={onTestWebhook} disabled={isLoading}>
              {isLoading ? 'Testing...' : 'Send Test Event'}
            </Button>
          </Box>

          {testResponse && testResponse.webhook_send_error && (
            <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
              <strong>Webhook Delivery Issue:</strong> {testResponse.webhook_send_error}
            </Alert>
          )}

          {testResponse && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Test Response Payload</Typography>
              <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                You should see the following response sent to your configured webhook URL.
              </Typography>
              <Paper elevation={2} sx={{ p: 2, backgroundColor: '#f5f5f5', overflowX: 'auto', maxHeight: '200px' }}>
                <pre>{JSON.stringify(testResponse, null, 2)}</pre>
              </Paper>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Verify Signature</Typography>
              <TextField
                fullWidth
                label="Enter Secret to Verify Signature"
                variant="outlined"
                value={testSecret}
                onChange={(e) => setTestSecret(e.target.value)}
                sx={{ mt: 1, mb: 1 }}
                size="small"
              />
              <Button variant="contained" onClick={onVerifySignature} sx={{ mr: 1 }}>
                Verify Signature
              </Button>
              {verificationResult && (
                <Typography sx={{ mt: 1, display: 'inline-block', color: verificationResult.startsWith('Signature Verified') ? 'green' : 'red' }}>
                  {verificationResult}
                </Typography>
              )}
            </Box>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <Typography variant="body1" paragraph>
            Use the following JavaScript example (with a library like <code>crypto-js</code>) to verify the signature on your server:
          </Typography>
          <Paper elevation={2} sx={{ p: 2, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
            <pre><code>{hmacExampleCode}</code></pre>
          </Paper>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
